import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, Flag, Pencil, Trash2, Loader2 } from "lucide-react";
import { fetchReviews, saveReview, deleteMyReview, reportReview, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const errMsg = (e) => formatApiError(e?.response?.data?.detail) || "Something went wrong";

export function Stars({ value, size = "w-3 h-3", onPick, testIdPrefix }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <button key={i} type="button" disabled={!onPick} onClick={() => onPick?.(i + 1)}
          data-testid={testIdPrefix ? `${testIdPrefix}-${i + 1}` : undefined}
          className={onPick ? "transition-transform hover:scale-125" : "cursor-default"}>
          <Star className={`${size} ${i < Math.round(value) ? "fill-[#FFE300] text-[#FFE300]" : "text-white/20"}`} />
        </button>
      ))}
    </div>
  );
}

function Summary({ summary }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-6 pb-6 border-b border-white/5" data-testid="reviews-summary">
      <div className="text-center sm:text-left">
        <div className="font-heading font-black text-5xl" data-testid="reviews-average">{summary.count ? summary.average.toFixed(1) : "—"}</div>
        <div className="flex justify-center sm:justify-start mt-1"><Stars value={summary.average} /></div>
        <div className="mono text-[10px] text-white/40 mt-1" data-testid="reviews-count">{summary.count} review{summary.count === 1 ? "" : "s"}</div>
      </div>
      <div className="flex-1 space-y-1 max-w-sm">
        {[5, 4, 3, 2, 1].map((n) => {
          const c = summary.distribution[String(n)] || 0;
          const pct = summary.count ? (c / summary.count) * 100 : 0;
          return (
            <div key={n} className="flex items-center gap-2 mono text-[10px] text-white/50">
              <span className="w-3">{n}</span>
              <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-[#FFE300]" style={{ width: `${pct}%` }} />
              </div>
              <span className="w-6 text-right">{c}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReviewForm({ slug, mine, onSaved, onCancel }) {
  const [rating, setRating] = useState(mine?.rating || 0);
  const [title, setTitle] = useState(mine?.title || "");
  const [body, setBody] = useState(mine?.body || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error("Pick a star rating first"); return; }
    setBusy(true);
    try {
      const r = await saveReview(slug, { rating, title, body });
      toast.success(r.updated ? "Review updated" : "Thanks for your review!");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="glass rounded-2xl p-5 space-y-3 border border-[#00F0FF]/20" data-testid="review-form">
      <div className="mono text-[10px] uppercase tracking-widest text-[#00F0FF]">{mine ? "Edit your review" : "Rate this app"}</div>
      <Stars value={rating} size="w-7 h-7" onPick={setRating} testIdPrefix="review-star" />
      <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} placeholder="Headline (optional)"
        data-testid="review-title-input" className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm focus:border-[#00F0FF]/60 outline-none" />
      <textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={2000} rows={3} placeholder="What did you like or dislike?"
        data-testid="review-body-input" className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm focus:border-[#00F0FF]/60 outline-none resize-none" />
      <div className="flex items-center gap-2">
        <button disabled={busy} data-testid="review-submit-btn"
          className="inline-flex items-center gap-2 bg-[#00F0FF] hover:bg-white text-black font-bold mono text-[10px] uppercase tracking-widest px-5 py-2.5 rounded-full transition-colors disabled:opacity-50">
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {mine ? "Update review" : "Post review"}
        </button>
        {onCancel && <button type="button" onClick={onCancel} className="mono text-[10px] uppercase tracking-widest text-white/50 hover:text-white px-3">Cancel</button>}
        <span className="ml-auto mono text-[10px] text-white/30">{body.length}/2000</span>
      </div>
    </form>
  );
}

function ReviewCard({ r, isMine, onEdit, onDelete, onReport }) {
  return (
    <div className="glass rounded-2xl p-5" data-testid={`review-card-${r.id}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-full flex items-center justify-center font-heading font-black"
          style={{ background: `${r.avatar_color}22`, color: r.avatar_color, border: `1px solid ${r.avatar_color}55` }}>
          {(r.user_name || "?")[0].toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="font-bold truncate">{r.user_name}{isMine && <span className="ml-2 mono text-[9px] text-[#00F0FF]">YOU</span>}</div>
          <div className="mono text-[10px] text-white/40">{(r.updated_at || r.created_at || "").slice(0, 10)}{r.edited ? " · edited" : ""}</div>
        </div>
        <div className="ml-auto"><Stars value={r.rating} /></div>
      </div>
      {r.title && <div className="font-heading font-bold text-lg">{r.title}</div>}
      {r.body && <p className="text-white/70 text-sm mt-1 whitespace-pre-line break-words">{r.body}</p>}
      <div className="flex gap-3 mt-3">
        {isMine ? (
          <>
            <button onClick={onEdit} data-testid="review-edit-btn" className="mono text-[10px] uppercase tracking-widest text-white/50 hover:text-[#00F0FF] inline-flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
            <button onClick={onDelete} data-testid="review-delete-btn" className="mono text-[10px] uppercase tracking-widest text-white/50 hover:text-[#FF003C] inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
          </>
        ) : (
          <button onClick={onReport} data-testid={`review-report-btn-${r.id}`} className="mono text-[10px] uppercase tracking-widest text-white/40 hover:text-[#FF003C] inline-flex items-center gap-1"><Flag className="w-3 h-3" /> Report</button>
        )}
      </div>
    </div>
  );
}

export default function ReviewsSection({ slug, testId, onCount }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [sort, setSort] = useState("newest");
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => fetchReviews(slug, sort).then(setData).catch(() => setData({ reviews: [], summary: { average: 0, count: 0, distribution: {} } })), [slug, sort]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (data && onCount) onCount(data.summary.count); }, [data, onCount]);

  if (!data) return <div className="mono text-xs text-[#00F0FF] animate-pulse">Loading reviews…</div>;
  const mine = data.mine;

  const remove = async () => {
    if (!window.confirm("Delete your review?")) return;
    try { await deleteMyReview(slug); toast.success("Review deleted"); setEditing(false); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const report = async (id) => {
    if (!user) { toast.error("Sign in to report reviews"); return; }
    const reason = window.prompt("Why are you reporting this review?", "Spam or inappropriate");
    if (reason === null) return;
    try { const r = await reportReview(id, reason); toast.success(r.already_reported ? "You already reported this" : "Reported — our moderators will take a look"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid={testId} className="space-y-5">
      <Summary summary={data.summary} />
      {!user ? (
        <div className="glass rounded-2xl p-5 text-sm text-white/70">
          <Link to="/login" className="text-[#00F0FF] font-bold" data-testid="review-login-link">Sign in</Link> to rate and review this app.
        </div>
      ) : mine && mine.status === "removed" ? (
        <div className="glass rounded-2xl p-4 text-sm text-[#FF003C]/80">Your review was removed by a moderator.</div>
      ) : (!mine || editing) ? (
        <ReviewForm key={mine?.id || "new"} slug={slug} mine={mine} onCancel={mine ? () => setEditing(false) : null}
          onSaved={() => { setEditing(false); load(); }} />
      ) : null}

      <div className="flex items-center gap-2 flex-wrap">
        <span className="mono text-[10px] uppercase tracking-widest text-white/40">Sort</span>
        {[["newest", "Newest"], ["highest", "Highest rated"], ["lowest", "Lowest rated"]].map(([k, l]) => (
          <button key={k} onClick={() => setSort(k)} data-testid={`reviews-sort-${k}`}
            className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${sort === k ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/10" : "border-white/10 text-white/50 hover:text-white"}`}>
            {l}
          </button>
        ))}
      </div>

      {data.reviews.length === 0 && <div className="mono text-xs text-white/40" data-testid="reviews-empty">No reviews yet — be the first.</div>}
      {data.reviews.map((r) => (
        <ReviewCard key={r.id} r={r} isMine={user && r.user_id === user.id}
          onEdit={() => setEditing(true)} onDelete={remove} onReport={() => report(r.id)} />
      ))}
    </div>
  );
}
